import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  FirestoreProductSubject,
  type KeywordFeedbackDetails,
} from "reedy-data/api";
import { FirestoreOrganisationMarketplace } from "reedy-data/models";
import { useFeatureFlags } from "contexts/FeatureFlagContext";
import { useProduct } from "contexts/ProductContext";
import { useAnalytics } from "contexts/AnalyticsContext";
import { useSession } from "contexts/AuthContext";
import { useKeywordsPipelineInfoDialog } from "contexts/KeywordsPipelineInfoDialogContext";
import {
  getKeywordsByType,
  getActionsByType,
} from "integrations/firebase/collections";
import { actionInProgress } from "utils/status";
import { logError } from "shared/services/ErrorReporting";
import { toast } from "utils/toast";
import { useSubjects } from "hooks/useSubjects";
import {
  findFinalMatch,
  phraseToWords,
  unifyKeywords,
} from "../../utils/keywordUtils";
import { useKeywordFeedback } from "../../hooks/useKeywordFeedback";
import { useKeywordGeneration } from "../../hooks/useKeywordGeneration";
import { UnifiedKeywordsTableView } from "./UnifiedKeywordsTableView";

interface UnifiedKeywordsTableProps {
  showReasoning?: boolean;
}

export const UnifiedKeywordsTable = ({
  showReasoning = false,
}: UnifiedKeywordsTableProps) => {
  const { t } = useTranslation(["components"]);
  const { product } = useProduct();
  const { features } = useFeatureFlags();
  const { gaEvent } = useAnalytics();
  const { organisation } = useSession();
  const { handleToggleSubject } = useSubjects();
  const { generateKeywords } = useKeywordGeneration();
  const { open: openPipelineInfoDialog } = useKeywordsPipelineInfoDialog();

  const onGenerate = useCallback(async () => {
    await generateKeywords();
  }, [generateKeywords]);

  const {
    database: databaseKeywords,
    generated: generatedKeywords,
    final: finalKeywords,
  } = getKeywordsByType(product);

  const isInFinal = useCallback(
    (subject: FirestoreProductSubject) =>
      findFinalMatch(subject, finalKeywords) !== undefined,
    [finalKeywords],
  );

  const onToggle = useCallback(
    async (subject: FirestoreProductSubject) => {
      const existing = findFinalMatch(subject, finalKeywords);
      const wasInFinal = existing !== undefined;
      try {
        await handleToggleSubject(existing ?? subject);
      } catch (error) {
        logError(error);
        toast.error(
          t("components:keywordsTab.unifiedKeywordsTable.toasts.errorUpdate"),
        );
        return;
      }
      if (wasInFinal) {
        gaEvent({
          type: "remove_keyword_from_final_selection",
          payload: {
            book: product?.id ?? "",
            keyword: subject.subjectHeadingText ?? "",
          },
        });
      } else {
        gaEvent({
          type: "add_keyword_from_main_table",
          payload: {
            book: product?.id ?? "",
            keyword: subject.subjectHeadingText ?? "",
            source: subject.source ?? "",
          },
        });
      }
    },
    [handleToggleSubject, finalKeywords, gaEvent, product, t],
  );

  const finalKeywordWordsArray = useMemo(() => {
    const words = new Set<string>();

    finalKeywords.forEach((kw) => {
      if (kw.subjectHeadingText) {
        phraseToWords(kw.subjectHeadingText).forEach((word) => words.add(word));
      }
    });
    return Array.from(words);
  }, [finalKeywords]);

  const { keywords: keywordsActions } = getActionsByType(product);
  const isGenerating = actionInProgress(keywordsActions?.generate);

  const hasGeneratedKeywords = generatedKeywords.length > 0;

  const feedback = useKeywordFeedback(product?.id);

  const wasGeneratingRef = useRef(isGenerating);
  useEffect(() => {
    if (isGenerating && !wasGeneratingRef.current) {
      feedback.clear();
    }
    wasGeneratingRef.current = isGenerating;
  }, [isGenerating, feedback]);

  const handleFeedbackSubmit = useCallback(
    async (keyword: string, details?: KeywordFeedbackDetails) => {
      await feedback.submit(keyword, details);
      if (!product) return;
      if (details) {
        gaEvent({
          type: "flag_keyword_with_category",
          payload: {
            book: product.id,
            keyword,
            category: details.category,
            subCategory: details.subCategory ?? undefined,
            hasFreeText: Boolean(details.freeText),
          },
        });
      } else {
        gaEvent({
          type: "flag_keyword_thumbs_down",
          payload: { book: product.id, keyword },
        });
      }
    },
    [feedback, gaEvent, product],
  );

  const handleFeedbackDiscard = useCallback(
    async (keyword: string) => {
      await feedback.discard(keyword);
      if (!product) return;
      gaEvent({
        type: "discard_keyword_feedback",
        payload: { book: product.id, keyword },
      });
    },
    [feedback, gaEvent, product],
  );

  const unifiedKeywords = useMemo(
    () => unifyKeywords(databaseKeywords, generatedKeywords),
    [databaseKeywords, generatedKeywords],
  );

  const isGermanMarketplace =
    organisation.settings?.marketplace === FirestoreOrganisationMarketplace.GER;

  return (
    <UnifiedKeywordsTableView
      data={{
        keywords: unifiedKeywords,
        finalKeywordWords: features.operationalAmazonRankKeywordsLimiter
          ? finalKeywordWordsArray
          : [],
        databaseKeywordsCount: databaseKeywords.length,
        hasGeneratedKeywords,
      }}
      status={{ isGenerating }}
      flags={{
        showReasoning,
        showScorePlaceholder: features.operationalAmazonRankKeywordsLimiter,
        showPipelineInfoButton: !isGermanMarketplace,
      }}
      isInFinal={isInFinal}
      onToggle={onToggle}
      onGenerate={onGenerate}
      onPipelineInfoOpen={openPipelineInfoDialog}
      getFeedbackEntry={feedback.getEntry}
      onSubmitFeedback={handleFeedbackSubmit}
      onDiscardFeedback={handleFeedbackDiscard}
    />
  );
};
